import { useApiQuery } from "@gladis/api-client";
import { HouseholdSettings } from "@gladis/domain-identity/ui";
import type { CurrentUserDto } from "@gladis/shared";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { Page } from "../../../components/page.js";
import { useT } from "@gladis/ui/i18n";

export const Route = createFileRoute("/_authenticated/_shell/settings_/household")({
  component: HouseholdSettingsPage,
});

/**
 * Household settings — the household, and who is in it.
 *
 * ==========================================================================================
 * ONE OF TWO PAGES WHERE THERE WAS ONE PAGE WITH TWO HALVES
 * ==========================================================================================
 *
 * `/settings` rendered `HouseholdSettings` and `ProfileSettings` stacked with a divider between them, and
 * its own comment already said why they are not the same thing: "`HouseholdSettings` is about the
 * household and changes when you switch, `ProfileSettings` and `DeleteAccountDialog` are about you and do
 * not." That is a boundary between two subjects, and on 2026-08-14 it became a boundary between two pages.
 *
 * The account menu offered both a day earlier as one route with a hash. A hash is a scroll position: it
 * cannot give either half a title of its own, and it leaves a member who wants their own name reading past
 * the household's member list to reach it.
 *
 * `settings_.household` rather than `settings.household`: the trailing underscore opts this route out of a
 * layout under `/settings`, which is the same thing `investments_.$stockId` does. `/settings` itself
 * remains, as a redirect — see that file.
 *
 * Composition only, as before. Every section comes from the identity package, which owns what it means and
 * which of them an Owner or an Admin may see.
 */
function HouseholdSettingsPage(): ReactNode {
  const t = useT();
  const navigate = useNavigate();
  // Served from the cache `_authenticated` already populated — no extra request.
  const me = useApiQuery<CurrentUserDto>(["me"], "/me");

  return (
    <Page>
      <Page.Title>{t("nav.householdSettings")}</Page.Title>
      <Page.Body>
        {/* Forms keep the narrow reading measure even though the column is wide — a text input
            stretched across 1400px is unusable. */}
        <div className="col-span-full max-w-content">
          {me.data ? (
            <HouseholdSettings
              me={me.data}
              onCreateHousehold={() => {
                void navigate({ to: "/onboarding" });
              }}
              onLeft={() => {
                // Leaving may have been their last membership, in which case the guard routes them
                // to onboarding. Navigating to the dashboard rather than deciding that here keeps
                // the "where does a member with no household go" rule in one place (FR-013).
                void navigate({ to: "/" });
              }}
            />
          ) : null}
        </div>
      </Page.Body>
    </Page>
  );
}
